import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api/http';
import { Modal } from './Modal';

interface Toast {
  id: number;
  message: string;
  tone: 'info' | 'error';
}

interface ConfirmRequest {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

interface FeedbackApi {
  notify: (message: string) => void;
  notifyError: (error: unknown) => void;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
}

const FeedbackContext = createContext<FeedbackApi | null>(null);

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Une erreur inattendue est survenue';
}

/** Toast notifications and confirmation dialogs, available through `useFeedback()`. */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pending, setPending] = useState<(ConfirmRequest & { resolve: (ok: boolean) => void }) | null>(null);
  const nextId = useRef(1);

  const push = useCallback((message: string, tone: Toast['tone']) => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, message, tone }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), tone === 'error' ? 5000 : 2800);
  }, []);

  const api: FeedbackApi = {
    notify: useCallback((message: string) => push(message, 'info'), [push]),
    notifyError: useCallback((error: unknown) => push(errorMessage(error), 'error'), [push]),
    confirm: useCallback((request: ConfirmRequest) => new Promise<boolean>((resolve) => setPending({ ...request, resolve })), []),
  };

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  return (
    <FeedbackContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.tone === 'error' ? 'error' : ''}`}>{t.message}</div>)}
      </div>
      {pending && (
        <Modal title={pending.title} onClose={() => close(false)}>
          <div className="muted">{pending.message}</div>
          <footer style={{ marginTop: 18 }}>
            <button type="button" className="btn" onClick={() => close(false)}>Annuler</button>
            <button type="button" className={`btn ${pending.danger ? 'danger' : 'primary'}`} onClick={() => close(true)}>{pending.confirmLabel ?? 'Confirmer'}</button>
          </footer>
        </Modal>
      )}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): FeedbackApi {
  const value = useContext(FeedbackContext);
  if (!value) throw new Error('useFeedback doit être utilisé dans FeedbackProvider');
  return value;
}
